<div class="cyber-company-setup">
  <div class="flashy-card company-card">
    <!-- Cyber Header -->
    <div class="cyber-card-header company-header">
      <div class="company-icon">
        <i class="fas fa-building"></i>
      </div>
      <h2>Company Setup</h2>
      <p class="company-description">
        Set up your organization to start using ScanNinja's cybersecurity
        platform
        @if (isTrialOrPremium) {
          <span class="tier-requirement">
            Complete business information is required to set up your account
            and billing.
          </span>
        }
      </p>
    </div>

    <!-- Company Setup Content -->
    <div class="company-content">
      <!-- Company Setup Form -->
      <form [formGroup]="companyForm" (ngSubmit)="onSubmit()" novalidate>
        <!-- Company Name -->
        <div class="cyber-input-wrapper mb-3">
          <label for="name" class="cyber-label">
            <i class="fas fa-building"></i>
            <span>Company Name <span class="text-danger">*</span></span>
          </label>
          <input title="Form input"
            type="text"
            id="name"
            class="form-control cyber-input"
            [class.is-invalid]="
              companyForm.get('name')?.invalid &&
              companyForm.get('name')?.touched
            "
            formControlName="name"
            placeholder="Enter your company name"
            />
          @if (
            companyForm.get('name')?.invalid &&
            companyForm.get('name')?.touched
            ) {
            <div
              class="invalid-feedback"
              >
              <i class="fas fa-exclamation-triangle me-1"></i>
              Company name is required
            </div>
          }
          <small class="form-text text-muted">
            <i class="fas fa-info-circle me-1"></i>
            This will be displayed across your ScanNinja platform
          </small>
        </div>

        <!-- Company Description -->
        <div class="cyber-input-wrapper mb-3">
          <label for="description" class="cyber-label">
            <i class="fas fa-align-left"></i>
            <span>Company Description</span>
            <span class="text-muted">(Optional)</span>
          </label>
          <textarea title="Form text area"
            id="description"
            class="form-control cyber-input cyber-textarea"
            formControlName="description"
            placeholder="Brief description of your company..."
            rows="3"
          ></textarea>
          <small class="form-text text-muted">
            <i class="fas fa-lightbulb me-1"></i>
            Help your team identify this organization
          </small>
        </div>

        <!-- Company Logo Upload -->
        <div class="cyber-input-wrapper mb-4">
          <label class="cyber-label" for="company-logo-uploader">
            <i class="fas fa-image"></i>
            <span>Company Logo</span>
            <span class="text-muted">(Optional)</span>
          </label>
          <div class="cyber-upload-area">
            <app-file-uploader-input
              id="company-logo-uploader"
              [directory]="companyUploadDirectory"
              (uploadComplete)="onUploadComplete($event)"
              >
            </app-file-uploader-input>

            @if (downloadURL) {
              <div class="logo-preview">
                <div class="preview-header">
                  <i class="fas fa-check-circle text-success me-2"></i>
                  <span>Logo Preview</span>
                </div>
                <img
                  [src]="downloadURL"
                  class="company-logo-preview"
                  alt="Company Logo Preview"
                  />
              </div>
            }
          </div>
          <small class="form-text text-muted">
            <i class="fas fa-cloud-upload-alt me-1"></i>
            Upload a logo to personalize your company profile
          </small>
        </div>

        <!-- Website (Optional) -->
        <div class="cyber-input-wrapper mb-3">
          <label for="website" class="cyber-label">
            <i class="fas fa-globe"></i>
            <span>Company Website</span>
            <span class="text-muted">(Optional)</span>
          </label>
          <input title="Form input"
            type="url"
            id="website"
            class="form-control cyber-input"
            formControlName="website"
            placeholder="https://yourcompany.com"
            />
          <small class="form-text text-muted">
            <i class="fas fa-link me-1"></i>
            Your company's main website URL
          </small>
        </div>

        <!-- Business Information Section (Trial/Premium only) -->
        @if (isTrialOrPremium) {
          <div class="business-info-section">
            <div class="section-header">
              <h4 class="section-title">
                <i class="fas fa-briefcase me-2"></i>
                Business Information
                <span class="badge bg-cyber-accent ms-2">Required</span>
              </h4>
              <p class="section-description">
                Complete business information is required to set up your account
                and billing.
              </p>
            </div>
            <!-- Industry & Company Size Row -->
            <div class="row">
              <div class="col-md-6">
                <div class="cyber-input-wrapper mb-3">
                  <label for="industry" class="cyber-label">
                    <i class="fas fa-industry"></i>
                    <span>Industry <span class="text-danger">*</span></span>
                  </label>
                  <select title="Form select"
                    id="industry"
                    class="form-select cyber-input"
                    [class.is-invalid]="isFieldInvalid('industry')"
                    formControlName="industry"
                    >
                    <option value="">Select your industry</option>
                    @for (industry of industryOptions; track industry) {
                      <option
                        [value]="industry"
                        >
                        {{ industry }}
                      </option>
                    }
                  </select>
                  <div class="invalid-feedback">
                    {{ getFieldError("industry") }}
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="cyber-input-wrapper mb-3">
                  <label for="companySize" class="cyber-label">
                    <i class="fas fa-users"></i>
                    <span>Company Size <span class="text-danger">*</span></span>
                  </label>
                  <select title="Form select"
                    id="companySize"
                    class="form-select cyber-input"
                    [class.is-invalid]="isFieldInvalid('companySize')"
                    formControlName="companySize"
                    >
                    <option value="">Select company size</option>
                    @for (size of companySizeOptions; track size) {
                      <option
                        [value]="size"
                        >
                        {{ size }} employees
                      </option>
                    }
                  </select>
                  <div class="invalid-feedback">
                    {{ getFieldError("companySize") }}
                  </div>
                </div>
              </div>
            </div>
            <!-- Business Type -->
            <div class="cyber-input-wrapper mb-3">
              <label for="businessType" class="cyber-label">
                <i class="fas fa-certificate"></i>
                <span>Business Type <span class="text-danger">*</span></span>
              </label>
              <select title="Form select"
                id="businessType"
                class="form-select cyber-input"
                [class.is-invalid]="isFieldInvalid('businessType')"
                formControlName="businessType"
                >
                <option value="">Select business type</option>
                @for (type of businessTypeOptions; track type) {
                  <option
                    [value]="type.toLowerCase()"
                    >
                    {{ type }}
                  </option>
                }
              </select>
              <div class="invalid-feedback">
                {{ getFieldError("businessType") }}
              </div>
              <small class="form-text text-muted">
                <i class="fas fa-info-circle me-1"></i>
                Legal structure of your organization
              </small>
            </div>
            <!-- Compliance Requirements -->
            <div class="cyber-input-wrapper mb-4">
              <label for="complianceRequirements" class="cyber-label">
                <i class="fas fa-shield-alt"></i>
                <span>Compliance Requirements</span>
                <span class="text-muted">(Optional)</span>
              </label>
              <select title="Form select"
                id="complianceRequirements"
                class="form-select cyber-input"
                formControlName="complianceRequirements"
                multiple
                >
                @for (compliance of complianceOptions; track compliance) {
                  <option
                    [value]="compliance.toLowerCase()"
                    >
                    {{ compliance }}
                  </option>
                }
              </select>
              <small class="form-text text-muted">
                <i class="fas fa-check-shield me-1"></i>
                Select any compliance standards your organization must meet
              </small>
            </div>
            <!-- Billing Address Section -->
            <div class="billing-address-section" formGroupName="billingAddress">
              <div class="section-header">
                <h5 class="section-title">
                  <i class="fas fa-map-marker-alt me-2"></i>
                  Billing Address
                  <span class="text-danger">*</span>
                </h5>
                <p class="section-description">
                  Required for account setup and billing.
                </p>
              </div>
              <!-- Street Address -->
              <div class="cyber-input-wrapper mb-3">
                <label for="street" class="cyber-label">
                  Street Address <span class="text-danger">*</span>
                </label>
                <input title="Form input"
                  #streetInput
                  type="text"
                  id="street"
                  class="form-control cyber-input"
                  [class.is-invalid]="isFieldInvalid('billingAddress.street')"
                  formControlName="street"
                  placeholder="Start typing your address…"
                  autocomplete="off"
                  role="combobox"
                  aria-autocomplete="list"
                  aria-controls="billing-address-suggestions"
                  [attr.aria-expanded]="suggestions.length > 0"
                [attr.aria-activedescendant]="
                  activeSuggestionIndex >= 0
                    ? suggestions[activeSuggestionIndex]?.id
                    : null
                "
                  (keydown)="onSuggestionKeydown($event)"
                  (blur)="closeSuggestions()"
                  />
                @if (suggestions.length) {
                  <div
                    id="billing-address-suggestions"
                    class="address-suggestions"
                    role="listbox"
                    aria-label="Address suggestions"
                    >
                    @for (suggestion of suggestions; track suggestion; let i = $index) {
                      <button
                        type="button"
                        [id]="suggestion.id"
                        class="address-suggestion"
                        [class.active]="i === activeSuggestionIndex"
                        role="option"
                        tabindex="-1"
                        [attr.aria-selected]="i === activeSuggestionIndex"
                        (mousedown)="$event.preventDefault()"
                        (click)="selectSuggestion(i)"
                        >
                        {{ suggestion.text }}
                      </button>
                    }
                  </div>
                }
                <div class="invalid-feedback">
                  {{ getFieldError("billingAddress.street") }}
                </div>
                <small class="form-text text-muted">
                  <i class="fas fa-search-location me-1"></i>
                  Suggestions are optional; city, state &amp; ZIP can also be entered manually
                </small>
              </div>
              <!-- City, State, Postal Code Row -->
              <div class="row">
                <div class="col-md-5">
                  <div class="cyber-input-wrapper mb-3">
                    <label for="city" class="cyber-label">
                      City <span class="text-danger">*</span>
                    </label>
                    <input title="Form input"
                      type="text"
                      id="city"
                      class="form-control cyber-input"
                      [class.is-invalid]="isFieldInvalid('billingAddress.city')"
                      formControlName="city"
                      placeholder="New York"
                      />
                    <div class="invalid-feedback">
                      {{ getFieldError("billingAddress.city") }}
                    </div>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="cyber-input-wrapper mb-3">
                    <label for="state" class="cyber-label">
                      State <span class="text-danger">*</span>
                    </label>
                    <select title="Form select"
                      id="state"
                      class="form-select cyber-input"
                      [class.is-invalid]="isFieldInvalid('billingAddress.state')"
                      formControlName="state"
                      >
                      <option value="">Select state</option>
                      @for (s of usStates; track s) {
                        <option
                          [value]="s.abbr"
                        >{{ s.name }}</option>
                      }
                    </select>
                    <div class="invalid-feedback">
                      {{ getFieldError("billingAddress.state") }}
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="cyber-input-wrapper mb-3">
                    <label for="postalCode" class="cyber-label">
                      Postal Code <span class="text-danger">*</span>
                    </label>
                    <input title="Form input"
                      type="text"
                      id="postalCode"
                      class="form-control cyber-input"
                    [class.is-invalid]="
                      isFieldInvalid('billingAddress.postalCode')
                    "
                      formControlName="postalCode"
                      placeholder="10001"
                      />
                    <div class="invalid-feedback">
                      {{ getFieldError("billingAddress.postalCode") }}
                    </div>
                  </div>
                </div>
              </div>
              <!-- Country (US only) -->
              <div class="cyber-input-wrapper mb-3">
                <label for="country" class="cyber-label">
                  Country
                  <span class="badge bg-secondary ms-2" style="font-size:0.65rem">US only</span>
                </label>
                <select title="Form select"
                  id="country"
                  class="form-select cyber-input"
                  formControlName="country"
                  >
                  <option value="US">United States</option>
                </select>
                <small class="form-text text-muted">
                  <i class="fas fa-globe-americas me-1"></i>
                  ScanNinja currently operates within the United States only
                </small>
              </div>
            </div>
          </div>
        }

        <!-- Server Message Display -->
        @if (serverMessage) {
          <div
            class="cyber-alert cyber-alert-danger mb-3"
            role="alert"
            >
            <i class="fas fa-exclamation-triangle"></i>
            <div class="cyber-alert-content">
              <div class="cyber-alert-title">Error</div>
              <div class="cyber-alert-message">{{ serverMessage }}</div>
            </div>
          </div>
        }

        <!-- Action Buttons -->
        <div class="company-actions">
          <button
            type="submit"
            class="btn btn-cyber-primary"
            [disabled]="loading || !companyForm.valid"
            >
            @if (loading) {
              <span
                class="spinner-border spinner-border-sm me-2"
              ></span>
            }
            @if (!loading) {
              <i class="fas fa-rocket me-2"></i>
            }
            {{ loading ? "Creating Company..." : "Create Company & Continue" }}
          </button>

          <!-- Tier Requirement Info -->
          @if (isTrialOrPremium && hasTierSpecificIncompleteFields()) {
            <div
              class="tier-info-banner mt-3"
              >
              <div class="cyber-alert cyber-alert-info">
                <i class="fas fa-info-circle"></i>
                <div class="cyber-alert-content">
                  <div class="cyber-alert-title">
                    Account Requirements
                  </div>
                  <div class="cyber-alert-message">
                    {{ getTierRequirementText() }}
                  </div>
                </div>
              </div>
            </div>
          }
        </div>
      </form>
    </div>

    <!-- Progress Indicator -->
    <div class="company-progress">
      <div class="progress-steps">
        <div class="step completed">
          <i class="fas fa-user-check"></i>
          <span>Account Created</span>
        </div>
        <div class="step completed">
          <i class="fas fa-user-edit"></i>
          <span>Profile Info</span>
        </div>
        <div class="step active">
          <i class="fas fa-building"></i>
          <span>Company Setup</span>
        </div>
        <div class="step">
          <i class="fas fa-tachometer-alt"></i>
          <span>Dashboard</span>
        </div>
      </div>
    </div>
  </div>
</div>
